import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { Crown, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

interface CheckoutSessionModalProps {
    open: boolean;
    onClose: () => void;
    sessionInfo: any;
    loading: boolean;
}

const CheckoutSessionModal: React.FC<CheckoutSessionModalProps> = ({
    open,
    onClose,
    sessionInfo,
    loading
}) => {
    const { t, i18n } = useTranslation();

    const formatDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString();

    const formatAmount = (amount: number, currency: string) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);

    const handleClose = () => {
        const url = new URL(window.location.href);
        if (url.searchParams.has('session_id')) {
            url.searchParams.delete('session_id');
            window.history.replaceState({}, '', url.toString());
        }
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent
                dir={getLanguageDirection(i18n.language)}
                className="max-w-md bg-card border-border rounded-2xl"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-foreground">
                        <Crown size={22} className="text-gold" />
                        {t('common.subscription.paymentConfirmation')}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-2">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-gold" />
                            <p className="text-sm text-muted-foreground">
                                {t('common.subscription.loadingPayment')}
                            </p>
                        </div>
                    ) : sessionInfo ? (
                        <div className="space-y-4">
                            {/* Success banner */}
                            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-800">
                                <CheckCircle size={16} className="shrink-0 mt-0.5" />
                                <p className="text-sm font-medium">{t('common.subscription.paymentSuccess')}</p>
                            </div>

                            {/* Payment details */}
                            <div className="rounded-xl border border-border bg-background p-4 space-y-2">
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    {t('common.subscription.paymentDetails')}
                                </h4>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t('common.subscription.amount')}</span>
                                    <span className="font-semibold text-foreground">
                                        {formatAmount(sessionInfo.amountTotal, sessionInfo.currency)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t('common.subscription.paymentStatus')}</span>
                                    <span className={`font-semibold ${sessionInfo.paymentStatus === 'paid' ? 'text-green-700' : 'text-foreground'}`}>
                                        {sessionInfo.paymentStatus?.charAt(0)?.toUpperCase() + sessionInfo.paymentStatus?.slice(1)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t('common.subscription.customerEmail')}</span>
                                    <span className="font-semibold text-foreground">{sessionInfo.customerEmail}</span>
                                </div>
                            </div>

                            {/* Line items */}
                            {sessionInfo.lineItems?.length > 0 && (
                                <div className="rounded-xl border border-border bg-background p-4 space-y-2">
                                    <h4 className="text-sm font-semibold text-foreground mb-3">
                                        {t('common.subscription.subscriptionItems')}
                                    </h4>
                                    {sessionInfo.lineItems.map((item: any, index: number) => (
                                        <div key={index} className="flex justify-between text-sm">
                                            <span className="text-foreground">
                                                {item.description || t('common.subscription.subscriptionPlan', 'Subscription Plan')}
                                            </span>
                                            <span className="font-semibold text-foreground">
                                                {formatAmount(item.amount_total, sessionInfo.currency)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            <p className="text-sm">{t('common.subscription.paymentError')}</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        onClick={handleClose}
                        className="bg-gold hover:bg-gold-bright text-white border-none"
                    >
                        {t('common.buttons.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CheckoutSessionModal;
